// Globals
import { useTranslation } from 'react-i18next';

// Components
import { Button, Spinner } from '@/ui';
import { CheckmarkCircleIcon } from '@/icons';

// Hooks
import { IUseSeoFilterEditor } from '../../hooks';

// Styles
import {
  StyledHeader,
  StyledHeaderText,
  StyledHeaderActions,
  StyledSavedBadge,
  StyledCategoryTitle,
  StyledSummary,
  StyledGroups,
  StyledGroupCard,
  StyledGroupHeader,
  StyledGroupTitle,
  StyledGroupCount,
  StyledToggleAll,
  StyledValues,
  StyledValueCheckbox,
  StyledPlaceholder,
} from './SeoFilterGroupsStyles';

export interface ISeoFilterGroupsProps {
  categoryName: string;
  canManage: boolean;
  editor: IUseSeoFilterEditor;
}

const SeoFilterGroups = ({ categoryName, canManage, editor }: ISeoFilterGroupsProps) => {
  const { t } = useTranslation(['seo']);
  const { groups, isLoading, isSaving, isDirty, selected, totalSelected, totalValues, toggleValue, toggleAll, save } =
    editor;

  if (isLoading) {
    return (
      <StyledPlaceholder>
        <Spinner />
      </StyledPlaceholder>
    );
  }

  if (!groups.length) {
    return <StyledPlaceholder>{t('seo_filters_no_filters')}</StyledPlaceholder>;
  }

  return (
    <>
      <StyledHeader>
        <StyledHeaderText>
          <StyledCategoryTitle>{categoryName}</StyledCategoryTitle>
          <StyledSummary>{t('seo_filters_summary', { selected: totalSelected, total: totalValues })}</StyledSummary>
        </StyledHeaderText>

        <StyledHeaderActions>
          {!isDirty && (
            <StyledSavedBadge>
              <CheckmarkCircleIcon />
              {t('seo_filters_saved_badge')}
            </StyledSavedBadge>
          )}
          {canManage && (
            <Button type="primary" loading={isSaving} disabled={!isDirty} onClick={save}>
              {t('seo_filters_save')}
            </Button>
          )}
        </StyledHeaderActions>
      </StyledHeader>

      <StyledGroups>
        {groups.map(({ filter, values }) => {
          const selectedIds = selected[filter.id] ?? [];
          const valueIds = values.map((value) => value.id);
          const allSelected = selectedIds.length === values.length && values.length > 0;

          return (
            <StyledGroupCard key={filter.id}>
              <StyledGroupHeader>
                <StyledGroupTitle>{filter.locale?.uk?.title || filter.slug}</StyledGroupTitle>
                <StyledGroupCount>
                  {t('seo_filters_group_count', { selected: selectedIds.length, total: values.length })}
                </StyledGroupCount>
                {canManage && (
                  <StyledToggleAll
                    type="button"
                    disabled={!values.length}
                    onClick={() => toggleAll(filter.id, valueIds)}
                  >
                    {allSelected ? t('seo_filters_disable_all') : t('seo_filters_enable_all')}
                  </StyledToggleAll>
                )}
              </StyledGroupHeader>

              <StyledValues>
                {values.map((value) => (
                  <StyledValueCheckbox
                    key={value.id}
                    checked={selectedIds.includes(value.id)}
                    disabled={!canManage}
                    onChange={(e) => toggleValue(filter.id, value.id, e.target.checked)}
                  >
                    {value.locale?.uk?.title || value.slug}
                  </StyledValueCheckbox>
                ))}
              </StyledValues>
            </StyledGroupCard>
          );
        })}
      </StyledGroups>
    </>
  );
};

export default SeoFilterGroups;
